<template>
  <div class="patient-journal--wrap">
    <preview-zoom-dialog
      :dialogType="dialogType"
      :imageSrc="imageSrc"
      :dialogFile="dialogFile"
      :dialogFileMlUrl="dialogFileMlUrl"
      :name="imageName"
      @reload-resource="reloadImageResource"
      @close-dialog="closeMediaDialog"
    />
    <div class="patient-journal">
      <v-container>
        <v-row>
          <v-col>
            <calendar
              :selected-date="selectedDate"
              :dot-marked-dates="journalDates"
              @selectDate="selectDate"
              @getCalendar="selectCalendar"
            />
          </v-col>
        </v-row>
        <div v-if="!currentJournalEmpty">
          <v-row>
            <v-col cols="12">
              <p class="section-title">
                {{ dateFormattedHuman.day }} {{ selectedDate.day }}<br />
                <span
                  >{{ dateFormattedHuman.month }} {{ selectedDate.year }}</span
                >
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="2" class="physical-effort--wrap"
              ><physical-effort :efforts="currentJournal.physicalEffort"
            /></v-col>
            <v-col cols="10">
              <meals
                :meals="currentJournal.meals"
                :participant-id="$route.params.id"
                :date="dateFormattedAPI"
                @updateMeal="updateMeal($event)"
                @show-bigger-image="showBiggerImage"
                @addSelectedMealComment="addMealComment"
              />
            </v-col>
          </v-row>
        </div>
        <v-row class="patient-journal--not-found" v-else>
          <v-col cols="12">
            <p class="section-title">
              {{ dateFormattedHuman.day }} {{ selectedDate.day }}<br />
              <span
                >{{ dateFormattedHuman.month }} {{ selectedDate.year }}</span
              >
            </p>
            <h2>Journal for this day is empty</h2>
          </v-col>
        </v-row>
        <v-dialog
          v-model="commitmentHover"
          hide-overlay
          content-class="meal-hover-dialog"
          @click:outside="onClickOutside"
          @keydown="onKeydown"
        >
          <v-card>
            <img v-if="commitmentHoverUrl" :src="commitmentHoverUrlComputed" />
            <v-btn
              color="white"
              icon
              size="24"
              @click="commitmentPhotoCloseHover"
            >
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </v-card>
        </v-dialog>
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Calendar from '@/components/journal/Calendar.vue'
import PhysicalEffort from '@/components/journal/PhysicalEffort.vue'
import Meals from '@/components/journal/Meals.vue'
import { emojiDictionary } from '@/services/MessageService'
import ReactionsMixin from '@/mixins/reactions'
import PreviewZoomDialog from '@/components/dialogs/PreviewZoomDialog.vue'
import isStringBase64 from '@/helpers/isStringBase64.js'

// @ is an alias to /src
export default {
  name: 'PatientJournal',
  components: {
    Calendar,
    PhysicalEffort,
    Meals,
    PreviewZoomDialog,
  },
  mixins: [ReactionsMixin],
  data: () => ({
    today: new Date(),
    emoji: emojiDictionary,
    selectedDate: {
      year: '',
      month: '',
      day: '',
    },
    isEditing: false,
    showEmoji: false,
    newGeneralComment: false,
    oldGeneralComment: false,
    currentFile: null,
    dialogType: 0,
    imageSrc: false,
    imageName: false,
    dialogFile: null,
    dialogFileMlUrl: null,
    commitmentPicture: null,
    commitmentHover: false,
    commitmentHoverUrl: false,
    commitmentHoverTimeout: false,
    commitmentHoverInterval: false,
    progressBarsValue: 0,
  }),
  watch: {
    currentPatient(val) {
      if (val && String(this.$route.params?.id) === String(val?.id)) {
        this.getCalendarData()
      }
    },
    $route: {
      handler(newVal, oldVal) {
        // get new current patient data when patient id changed
        if (
          newVal.params?.id !== undefined &&
          oldVal.params?.id !== newVal.params?.id
        ) {
          this.getPatient(newVal.params.id)
        }
        if (newVal?.query?.date) {
          this.getCalendarData()
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState('patientsStates', [
      'currentPatient',
      'journals',
      'currentJournal',
      'journalDates',
    ]),
    ...mapState('resourcesStates', ['resource']),
    currentJournalEmpty() {
      // api returns empty object for given date, when journal was not entered that day
      if (
        this.currentJournal.physicalEffort &&
        this.currentJournal.physicalEffort.journalId
      )
        return false
      else return true
    },
    getQueryParams() {
      return this.$route.query
    },
    getFullPath() {
      return this.$route.path
    },
    dateFormattedHuman() {
      let tmpDate = {
        year: this.selectedDate.year,
        month: this.selectedDate.month - 1,
        day: this.selectedDate.day,
      }
      let date = this.$moment(tmpDate)
      let tmp = {}
      tmp.day = date.format('dddd')
      tmp.month = date.format('MMMM')
      return tmp
    },
    dateFormattedAPI() {
      let tmpDate = {
        year: this.selectedDate.year,
        month: this.selectedDate.month - 1,
        day: this.selectedDate.day,
      }
      let date = this.$moment(tmpDate)
      return date.format('YYYY-MM-DD')
    },
    defaultQueryParams() {
      let tmpDate = {
        year: this.selectedDate.year,
        month: this.selectedDate.month - 1,
        day: this.selectedDate.day,
      }
      let date = this.$moment(tmpDate)
      return {
        participantId: this.$route.params.id,
        from: this.selectedDate.year + '-' + date.format('MM') + '-01',
        to:
          this.selectedDate.year +
          '-' +
          date.format('MM') +
          '-' +
          date.endOf('month').format('DD'),
      }
    },
    generalComment: {
      get() {
        if (!this.newGeneralComment && !this.isEditing)
          return this.currentJournal.comment
        else return this.newGeneralComment
      },
      set(val) {
        if (!this.oldGeneralComment) {
          this.oldGeneralComment = this.generalComment
        }
        this.newGeneralComment = val
      },
    },
    commitmentHoverUrlComputed() {
      if (this.commitmentPicture) {
        if (this.commitmentPicture.previewUrl) {
          if (this.isStringBase64(this.commitmentPicture.previewUrl)) {
            return 'data:image/jpeg;base64,' + this.commitmentPicture.previewUrl
          }
          return this.commitmentPicture.previewUrl
        } else {
          if (this.isStringBase64(this.commitmentHoverUrl)) {
            return 'data:image/jpeg;base64,' + this.commitmentHoverUrl
          }
          return this.commitmentHoverUrl
        }
      } else {
        return ''
      }
    },
  },
  created() {
    this.getCalendarData()
  },
  methods: {
    isStringBase64,
    ...mapActions('patientsStates', [
      'getPatient',
      'getCalendar',
      'getJournal',
      'changeJournal',
      'changeJournalMeal',
      'addJournalMealComment',
    ]),
    ...mapActions('resourcesStates', ['getResource']),
    selectCalendar({ month, year }) {
      let date = this.$moment({ year: year, month: month - 1, day: 1 })
      this.getCalendar({
        participantId: this.$route.params.id,
        from: date.format('YYYY-MM-01'),
        to: date.endOf('month').format('YYYY-MM-DD'),
      })
    },
    selectDate(newDate) {
      this.selectedDate = { ...newDate }
      this.generalComment = false
      this.oldGeneralComment = false
      this.newGeneralComment = false
      this.commitmentPicture = null
      this.getJournal({
        id: this.$route.params.id,
        date: this.dateFormattedAPI,
      }).then(() => {
        if (this.$route.query?.date != this.dateFormattedAPI) {
          this.$router.replace({
            path: this.$route.path,
            query: { date: this.dateFormattedAPI },
          })
        }
        if (
          this.currentJournal.commitment &&
          this.currentJournal.commitment.resourceId
        ) {
          this.getResource({
            id: this.currentJournal.commitment.resourceId,
          }).then(() => {
            this.commitmentPicture = { ...this.resource }
          })
        }
      })
    },
    onInput(value) {
      this.showEmoji = false
    },
    addGeneralComment(comment) {
      this.isEditing = false
      this.currentJournal.comment = this.newGeneralComment
      this.oldGeneralComment = ''
      this.changeJournal({
        id: this.$route.params.id,
        date: this.dateFormattedAPI,
        value: this.newGeneralComment,
      }).then(() => {
        this.getJournal({
          id: this.$route.params.id,
          date: this.dateFormattedAPI,
        })
        this.generalComment = false
        this.oldGeneralComment = false
        this.newGeneralComment = false
      })
    },
    cancelGeneralComment(comment) {
      this.isEditing = false
      if (this.oldGeneralComment) {
        this.newGeneralComment = this.oldGeneralComment
      }
      this.oldGeneralComment = false
      this.newGeneralComment = false
    },
    onBlur(e) {
      this.showEmoji = false
    },
    toggleEditGeneralComment() {
      this.newGeneralComment = this.generalComment
      this.isEditing = !this.isEditing
    },
    addEmoji(e, emoji) {
      e.preventDefault()
      const textField = this.$refs.textField.$refs.input
      const cursorPosition = textField.selectionStart
      const len = this.newGeneralComment.length
      const before = this.newGeneralComment.substr(0, cursorPosition)
      const after = this.newGeneralComment.substr(cursorPosition, len)

      this.newGeneralComment = before + emoji + after
      this.setCursorPosition(cursorPosition + emoji.length)
    },
    showEmojiHandler(e) {
      e.preventDefault()
      this.showEmoji = !this.showEmoji
    },
    setCursorPosition(pos) {
      this.$nextTick().then(() => {
        this.$refs.textField.$refs.input.selectionStart = pos
        this.$refs.textField.$refs.input.selectionEnd = pos
      })
    },
    updateMeal(meal) {
      const { picture, ...mealWithoutPicture } = meal
      this.changeJournalMeal({
        id: this.$route.params.id,
        date: this.dateFormattedAPI,
        meal: mealWithoutPicture,
      })
    },
    addMealComment(tmp) {
      this.addJournalMealComment({
        id: this.$route.params.id,
        date: this.dateFormattedAPI,
        mealId: tmp.mealId,
        comment: tmp.comment,
      })
    },
    reloadResource(id) {
      this.getResource({ id: id })
    },
    showBiggerImage({ url, file, name, mlResourceUrl = null, type = 1 }) {
      this.imageSrc = url
      this.imageName = name
      this.dialogFile = file
      this.dialogFileMlUrl = mlResourceUrl
      this.dialogType = type
    },
    async reloadImageResource(id) {
      await this.reloadResource(id)
      setTimeout(() => {
        this.imageSrc = this.resource.fileUrl
        this.imageName = this.resource.name
      }, 2000)
    },
    closeMediaDialog() {
      this.dialogType = 0
      this.imageName = null
      this.currentFile = null
      this.imageSrc = null
      this.dialogFileMlUrl = null
    },
    commitmentPhotoOpenHover() {
      clearTimeout(this.commitmentHoverTimeout)
      if (!this.commitmentHover) {
        this.progressBarsValue = 1
        if (this.commitmentHoverInterval)
          clearInterval(this.commitmentHoverInterval)
        this.commitmentHoverInterval = setInterval(() => {
          if (this.progressBarsValue >= 100) {
            clearInterval(this.commitmentHoverInterval)
            return
          }
          let tmp = this.progressBarsValue + 33
          if (tmp > 100) tmp = 100
          this.progressBarsValue = tmp
        }, 200)
        if (this.commitmentHoverTimeout)
          clearTimeout(this.commitmentHoverTimeout)
        this.commitmentHoverTimeout = setTimeout(() => {
          this.commitmentHover = null
          if (this.commitmentHover === null) {
            this.commitmentHoverUrl =
              this.commitmentPicture.previewUrl ||
              this.commitmentPicture.thumbnailUrl // in case preview is not available
            this.commitmentHover = true
            this.progressBarsValue = 0
          }
        }, 1000)
      }
    },
    commitmentPhotoClearHover() {
      this.progressBarsValue = 0

      clearInterval(this.commitmentHoverInterval)
      if (!this.commitmentHover) {
        clearTimeout(this.commitmentHoverTimeout)
        this.commitmentHover = false
        this.commitmentHoverUrl = null
      }
    },
    commitmentPhotoCloseHover() {
      this.commitmentHover = false
    },
    getCalendarData() {
      if (this.$route?.query?.date) {
        const tmpDate = this.$moment(this.$route.query.date, 'YYYY-MM-DD')
        this.selectedDate = {
          year: parseInt(tmpDate.format('YYYY')),
          month: parseInt(tmpDate.format('MM')),
          day: parseInt(tmpDate.format('DD')),
        }
      } else {
        this.selectedDate = {
          year: this.today.getFullYear(),
          month: this.today.getMonth() + 1,
          day: this.today.getDate(),
        }
      }
      this.getCalendar(this.defaultQueryParams),
        this.getJournal({
          id: this.$route.params.id,
          date: this.dateFormattedAPI,
        }).then(() => {
          if (
            this.currentJournal.commitment &&
            this.currentJournal.commitment.resourceId
          ) {
            this.getResource({
              id: this.currentJournal.commitment.resourceId,
            }).then(() => {
              this.commitmentPicture = { ...this.resource }
            })
          }
        })
    },
    onClickOutside() {
      this.commitmentPhotoCloseHover()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.commitmentPhotoCloseHover()
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.patient-journal {
  flex: 1;
  padding: 0 10px;
  height: 100%;
  &--wrap {
    height: 100%;
  }
  &--not-found {
    h2 {
      margin-top: 30px;
    }
  }
  & > .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .section-title {
    font-size: 1.428572rem;
    line-height: 1.2;
    padding-bottom: 15px;
    span {
      color: $bh-gray;
      font-size: 1rem;
      font-weight: normal;
    }
  }
  .section-subtitle {
    color: $bh-gray;
    font-size: 1.428572rem;
    margin-bottom: 0;
    &.bordered {
      border-top: 1px solid $bh-blue--4;
      margin-top: 25px;
      padding-top: 18px;
    }
  }
  .physical-effort--wrap {
    flex-grow: 0;
  }
  .general-comment {
    background: $bh-gray--6 !important;
    color: $bh-blue--4;
    &--wrap {
      position: relative;
      padding-top: 25px;
      p {
        margin-bottom: 0;
        img {
          width: 24px;
        }
        span {
          background: $bh-blue--4;
          padding: 13px 13px;
          margin-left: 12px;
          border-radius: 8px;
          display: inline-block;
          vertical-align: top;
        }
        .v-icon {
          margin-left: 10px;
          width: 20px;
          height: 20px;
          &::after {
            width: 30px;
            height: 30px;
            left: -5px;
            top: -3px;
            transform: scale(1);
          }
        }
      }
      .v-input__append-outer {
        margin-top: 0 !important;
        margin-bottom: 0;
      }
    }
    &--emoji {
      height: 100px;
      position: absolute;
      bottom: 100%;
      overflow-y: auto;
      padding: $gutter 0;
    }
  }
  .patient-comment {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    &__avatar {
      width: 24px;
      vertical-align: top;
      border-radius: 8px;
      margin-top: 10px;
      height: 24px;
      object-fit: cover;
    }
    span {
      display: inline-block;
      background: $bh-gray--6;
      padding: 13px 15px;
      margin-left: 15px;
      margin-top: 10px;
      border-radius: 8px;
    }
  }
  .commitment-photo {
    max-width: 160px;
    max-height: 160px;
    display: block;
    &__wrapper {
      position: relative;
      display: inline-block;
    }
    &.clickable {
      cursor: pointer;
    }
    &.loading {
      position: relative;
      .v-progress-circular {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0px);
      }
    }
  }
}
</style>
